import React, { useState } from 'react';
import { MatrixData, TextZone } from './MatrixConfigurator';
import { Trash2, Plus, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
interface ConfiguratorFormProps {
  matrixData: MatrixData;
  onAddZone: () => void;
  onRemoveZone: (id: string) => void;
  onZoneChange: (id: string, field: keyof TextZone, value: string | number) => void;
}
const removeAccents = (str: string): string => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};
export function ConfiguratorForm({
  matrixData,
  onAddZone,
  onRemoveZone,
  onZoneChange
}: ConfiguratorFormProps) {
  const [collapsedZones, setCollapsedZones] = useState<Set<string>>(new Set());
  const toggleZoneCollapse = (zoneId: string) => {
    setCollapsedZones(prev => {
      const newSet = new Set(prev);
      if (newSet.has(zoneId)) {
        newSet.delete(zoneId);
      } else {
        newSet.add(zoneId);
      }
      return newSet;
    });
  };
  const fixedPositions = [{
    value: -90,
    label: '12 heures'
  }, {
    value: 0,
    label: '3 heures'
  }, {
    value: 90,
    label: '6 heures'
  }, {
    value: 180,
    label: '9 heures'
  }];
  const handleTextChange = (zoneId: string, value: string) => {
    const cleanedText = removeAccents(value);
    onZoneChange(zoneId, 'text', cleanedText);
  };
  const getMaxRadius = (fontSize: number): number => {
    const marginSize = 4;
    const outerRadius = 180;
    const workingRadius = outerRadius - marginSize;
    const textBuffer = fontSize * 0.8;
    return Math.floor(workingRadius - textBuffer);
  };
  return <div className="rounded-xl border-2 bg-white p-6 shadow-sm transition-shadow hover:shadow-md" style={{
    borderColor: '#E5E7EB'
  }} data-id="element-24">
      <div className="mb-6 flex items-center justify-between" data-id="element-25">
        <div data-id="element-26">
          <div className="mb-1 text-sm font-bold uppercase tracking-wide" style={{
          color: '#6B4423'
        }} data-id="element-27">
            Zones de Texte
          </div>
          <div className="text-xs" style={{
          color: '#9CA3AF'
        }} data-id="element-28">
            Maximum 70 caractères par zone
          </div>
        </div>
        <button onClick={onAddZone} className="flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold text-white transition-all hover:opacity-90 hover:shadow-md" style={{
        backgroundColor: '#6B4423'
      }} data-id="element-29">
          <Plus size={18} data-id="element-30" />
          Ajouter
        </button>
      </div>
      <div className="space-y-4" data-id="element-31">
        {matrixData.textZones.map((zone, index) => {
        const isCollapsed = collapsedZones.has(zone.id);
        const maxRadius = getMaxRadius(zone.fontSize);
        return <div key={zone.id} className="rounded-lg border-2 bg-gray-50 p-5 transition-all" style={{
          borderColor: '#E5E7EB'
        }} data-id="element-32">
              <div className="mb-4 flex items-center justify-between" data-id="element-33">
                <button onClick={() => toggleZoneCollapse(zone.id)} className="flex items-center gap-2 text-sm font-bold transition-colors hover:opacity-70" style={{
              color: '#000000'
            }} data-id="element-34">
                  {isCollapsed ? <ChevronDown size={18} data-id="element-35" /> : <ChevronUp size={18} data-id="element-36" />}
                  {zone.text || `Zone ${index + 1}`}
                </button>
                {matrixData.textZones.length > 1 && <button onClick={() => onRemoveZone(zone.id)} className="rounded-lg p-2 transition-all hover:bg-red-50" style={{
              color: '#DC2626'
            }} data-id="element-37">
                    <Trash2 size={18} data-id="element-38" />
                  </button>}
              </div>
              {!isCollapsed && <div className="space-y-4" data-id="element-39">
                  <input type="text" value={zone.text} onChange={e => handleTextChange(zone.id, e.target.value)} maxLength={70} placeholder="Texte à graver ici" className="w-full rounded-lg border-2 bg-white p-3 text-sm transition-all focus:outline-none focus:ring-2" style={{
              borderColor: '#D1D5DB'
            }} onFocus={e => {
              e.target.style.borderColor = '#6B4423';
              e.target.style.boxShadow = '0 0 0 3px rgba(107, 68, 35, 0.1)';
            }} onBlur={e => {
              e.target.style.borderColor = '#D1D5DB';
              e.target.style.boxShadow = 'none';
            }} data-id="element-40" />
                  <div data-id="element-41">
                    <div className="mb-2 flex items-center justify-between" data-id="element-42">
                      <span className="text-xs font-semibold uppercase tracking-wide" style={{
                  color: '#6B4423'
                }} data-id="element-43">
                        Distance du centre
                      </span>
                      <span className="text-sm font-bold" style={{
                  color: '#000000'
                }} data-id="element-44">
                        {zone.radius}
                      </span>
                    </div>
                    <input type="range" min="50" max={maxRadius} value={Math.min(zone.radius, maxRadius)} onChange={e => onZoneChange(zone.id, 'radius', parseInt(e.target.value))} className="w-full" style={{
                accentColor: '#6B4423'
              }} data-id="element-45" />
                  </div>
                  <div data-id="element-46">
                    <div className="mb-3 text-xs font-semibold uppercase tracking-wide" style={{
                color: '#6B4423'
              }} data-id="element-47">
                      Position angulaire
                    </div>
                    <div className="grid grid-cols-2 gap-3" data-id="element-48">
                      {fixedPositions.map(position => <label key={position.value} className="flex cursor-pointer items-center justify-center rounded-lg border-2 p-3 text-xs font-semibold transition-all hover:shadow-md" style={{
                  borderColor: zone.angle === position.value ? '#6B4423' : '#D1D5DB',
                  backgroundColor: zone.angle === position.value ? '#FEF3F2' : 'white',
                  color: '#000000'
                }} data-id="element-49">
                          <input type="radio" name={`angle-${zone.id}`} value={position.value} checked={zone.angle === position.value} onChange={() => onZoneChange(zone.id, 'angle', position.value)} className="hidden" data-id="element-50" />
                          {position.label}
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-51">
                    <div className="mb-2 flex items-center justify-between" data-id="element-52">
                      <span className="text-xs font-semibold uppercase tracking-wide" style={{
                  color: '#6B4423'
                }} data-id="element-53">
                        Taille du texte
                      </span>
                      <span className="text-sm font-bold" style={{
                  color: '#000000'
                }} data-id="element-54">
                        {zone.fontSize}px
                      </span>
                    </div>
                    <input type="range" min="8" max="20" value={zone.fontSize} onChange={e => {
                const newFontSize = parseInt(e.target.value);
                onZoneChange(zone.id, 'fontSize', newFontSize);
                const newMaxRadius = getMaxRadius(newFontSize);
                if (zone.radius > newMaxRadius) {
                  onZoneChange(zone.id, 'radius', newMaxRadius);
                }
              }} className="w-full" style={{
                accentColor: '#6B4423'
              }} data-id="element-55" />
                  </div>
                  <button onClick={() => onZoneChange(zone.id, 'isInverted', !zone.isInverted)} className="flex w-full items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-semibold transition-all hover:shadow-md" style={{
              borderColor: zone.isInverted ? '#6B4423' : '#D1D5DB',
              backgroundColor: zone.isInverted ? '#FEF3F2' : 'white',
              color: '#000000'
            }} data-id="element-56">
                    <RotateCcw size={16} data-id="element-57" />
                    {zone.isInverted ? 'Sens de lecture inversé' : 'Sens de lecture normal'}
                  </button>
                </div>}
            </div>;
      })}
      </div>
    </div>;
}